import React from "react";
import { useNavigate } from "react-router";
import { X } from "lucide-react";
import { useSidebar } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import usePlanStore from "@/stores/planStore";
import EmergencyHomeIcon from "@/components/icons/EmergencyHomeIcon";
import { getRenewalInfo, isContractExpired } from "@/utils/contract";

const BODY_COPY =
  "Renueva tu plan PRO y asegura que tus anuncios sigan visibles para miles de compradores y arrendatarios.";

const buildHeadline = (info) => {
  if (!info) return "Tu plan PRO ha expirado.";
  if (info.daysLeft <= 0) return "Hoy es tu último día de beneficios PRO.";
  if (info.daysLeft === 1) return "Te queda 1 día de beneficios PRO.";
  return `Te quedan ${info.daysLeft} días de beneficios PRO.`;
};

/**
 * Global banner driven by the top-level `contract_status` from GET /clients/plan/.
 * During the "expired" renewal window the client keeps full access, so this is a
 * reassuring, non-blocking reminder to renew before the grace period closes.
 * Visual language follows the CyT PRO renewal banner (Figma node 750:3047).
 */
export default function ContractStatusBanner() {
  const navigate = useNavigate();
  const { isMobile, state } = useSidebar();
  const contractStatus = usePlanStore((s) => s.contractStatus);
  const contractEndDate = usePlanStore((s) => s.contractEndDate);
  const [dismissed, setDismissed] = React.useState(false);

  if (dismissed || !isContractExpired(contractStatus)) return null;

  const headline = buildHeadline(getRenewalInfo(contractEndDate));
  // The floating sidebar trigger sits over the top-left of the content whenever the
  // sidebar is collapsed (desktop) or on mobile; add clearance so it can't cover the banner.
  const needsTriggerClearance = isMobile || state === "collapsed";

  return (
    <div
      role="alert"
      className={cn(
        "relative mb-4 rounded-lg bg-violet-cyt-strong p-4 text-white",
        needsTriggerClearance && "mt-12 sm:mt-14",
      )}
    >
      {/* Column on mobile (close floats top-right, then icon+text, then full-width CTA);
          single centered row on desktop (icon, text, CTA, close). */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-6">
        <div className="flex items-start gap-4 pr-8 md:flex-1 md:items-center md:gap-6 md:pr-0">
          <EmergencyHomeIcon className="size-6 shrink-0" />
          <div className="flex flex-col gap-0.5 md:flex-row md:items-center md:gap-4 md:flex-1">
            <p className="text-base font-bold md:shrink-0 md:text-xl">{headline}</p>
            <p className="text-base md:flex-1">{BODY_COPY}</p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => navigate("/subscription")}
          className="w-full shrink-0 rounded-lg border border-[#acbebe]/80 bg-white px-6 py-2 text-base font-medium text-black transition-colors hover:bg-zinc-100 md:w-auto"
        >
          Renovar Plan
        </button>

        <button
          type="button"
          aria-label="Cerrar"
          onClick={() => setDismissed(true)}
          className="absolute right-3 top-3 text-white/80 transition-colors hover:text-white md:static md:self-center"
        >
          <X className="size-5 md:size-6" />
        </button>
      </div>
    </div>
  );
}
